/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ChatContainer` for the Caintra Connect frontend.
 * @module components/Chat/ChatContainer
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../../utils/logger.js";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styles from "../../styles/Chat.module.css";
import ReportUserModal from "../../modals/ReportUser/index.jsx";
import ReassignChatModal from "../../modals/ReassignChatModal/ReassignChatModal.jsx";
import ChatMessage from "./ChatMessage.jsx";
import ConnectionRequestBanner from "./ConnectionRequestBanner.jsx";
import ChatHeader from "./ChatHeader.jsx";
import ChatFileUploadArea from "./ChatFileUploadArea.jsx";
import ChatInputBar from "./ChatInputBar.jsx";
import { completeChatReassignment } from "../../logic/chat/chatReassign.js";

const HIDE_EMAIL_REQUEST_TYPES = new Set([
  "businessEventConnectionRequest",
  "customerCompanyUserConnectionRequest",
]);

const ChatContainer = ({
  selectedChat,
  selectedUser,
  selectedCompany,
  chatMessages,
  messageInput,
  setMessageInput,
  messageEndRef,
  activeConnections,
  isReconnecting,
  isUserActive,
  showFileUpload,
  selectedFile,
  isFileUploading,
  isDragging,
  fileError,
  users,
  setChats,
  setSelectedChat,
  setSelectedUser,
  onSendMessage,
  onKeyPress,
  onAttachFilesClick,
  onFileUploadClose,
  onFileClick,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onBackToInbox,
  className,
  readOnly,
  headerTitleOverride,
  readOnlyRequestType,
  readOnlyRequest,
  onConnectionRequestsUpdate,
  onChatParticipantsUpdate,
  onClearReadOnlyChat,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [isAcceptButtonDisabled, setIsAcceptButtonDisabled] = useState(false);

  const isMobile = window.innerWidth <= 767;
  const hideUserEmail =
    readOnly && HIDE_EMAIL_REQUEST_TYPES.has(readOnlyRequestType);

  const toggleDropdown = (event) => {
    event.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleReassignChat = () => {
    logger.info("handleReassignChat called");
    setShowReassignModal(true);
    setShowDropdown(false);
  };

  const displayReportModal = () => {
    logger.info("displayReportModal called");
    setShowDropdown(false);
    setReportModalOpen(true);
  };

  const handleReassignToUser = async (reassignUser, result) => {
    logger.info("Reassigning chat to:", { data: reassignUser });
    await completeChatReassignment({
      result,
      navigate,
      setChats,
      setSelectedChat,
      setSelectedUser,
      setShowReassignModal,
    });
  };

  if ((!selectedChat && !readOnly) || !selectedUser) {
    return (
      <div className={`${styles.chatContainer} ${className || ""}`}>
        <div className={styles.noChatSelected}>
          {t("pages.chat.selectChat")}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.chatContainer} ${className || ""}`}>
      <ChatHeader
        t={t}
        styles={styles}
        selectedUser={selectedUser}
        selectedCompany={selectedCompany}
        activeConnections={activeConnections}
        isReconnecting={isReconnecting}
        isUserActive={isUserActive}
        headerTitleOverride={headerTitleOverride}
        hideUserEmail={hideUserEmail}
        showDropdown={showDropdown}
        onBackToInbox={onBackToInbox}
        onToggleDropdown={toggleDropdown}
        onReassignChat={handleReassignChat}
        onReportUser={displayReportModal}
      />
      <div
        className={`${styles.chatMessagesContainer} ${
          showFileUpload && !readOnly ? styles.hasFileUpload : ""
        }`}
      >
        {showFileUpload && !readOnly && (
          <ChatFileUploadArea
            styles={styles}
            isMobile={isMobile}
            selectedFile={selectedFile}
            isFileUploading={isFileUploading}
            isDragging={isDragging}
            fileError={fileError}
            onFileChange={onFileChange}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onFileClick={onFileClick}
          />
        )}
        <div className={styles.chatMessages}>
          {chatMessages.map((message, index) => (
            <ChatMessage
              key={index}
              message={message}
              index={index}
              users={users}
              styles={styles}
            />
          ))}
          <div ref={messageEndRef} style={{ height: "1px" }} />
        </div>
        {readOnly && (
          <ConnectionRequestBanner
            readOnlyRequestType={readOnlyRequestType}
            readOnlyRequest={readOnlyRequest}
            isAcceptButtonDisabled={isAcceptButtonDisabled}
            setIsAcceptButtonDisabled={setIsAcceptButtonDisabled}
            onConnectionRequestsUpdate={onConnectionRequestsUpdate}
            onClearReadOnlyChat={onClearReadOnlyChat}
            setChats={setChats}
            setChatParticipants={onChatParticipantsUpdate}
            styles={styles}
          />
        )}
        <ChatInputBar
          t={t}
          styles={styles}
          readOnly={readOnly}
          isFileUploading={isFileUploading}
          messageInput={messageInput}
          onAttachFilesClick={onAttachFilesClick}
          onMessageInputChange={(event) => setMessageInput(event.target.value)}
          onKeyPress={onKeyPress}
          onSendMessage={onSendMessage}
        />
      </div>
      <ReportUserModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        selectedChat={selectedChat}
        selectedUser={selectedUser}
        setChats={setChats}
        setSelectedChat={setSelectedChat}
        setSelectedUser={setSelectedUser}
      />

      <ReassignChatModal
        isOpen={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        onReassign={handleReassignToUser}
        selectedChat={selectedChat}
        selectedUser={selectedUser}
      />
    </div>
  );
};

export default ChatContainer;
